import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { LearningContentText } from '@quebon/shared-learning/components/common/LearningContentText'
import { stripShortPointPrefix, WarmupTextView } from '@/components/common/WarmupTextView'
import { useAuthStore } from '@/store/authStore'
import { hasLcPermission } from '@/utils/roleUtils'
import { useCoachingGuide } from '@/hooks/coaching/useCoachingGuide'
import { useRateEssentialCheck } from '@/hooks/coaching/useRateEssentialCheck'
import { EssentialCheckRatingInput } from './EssentialCheckRatingInput'
import { UnderstandingBody } from './UnderstandingBody'
import type { SubjectInfo } from '@/types/coaching'

interface Props {
  learnerId: number
  /** 포커스 레슨(오늘의 기본 포커스). null 이면 가이드 대상 레슨이 없음. */
  lessonId: number | null
  /** 포커스 레슨 완료 여부 — 개념·원리 이해도(6b) 문구 분기(기획: "레슨이 완료되면 자동 표시"). */
  lessonCompleted: boolean
  surface?: 'page' | 'dialog'
}

/**
 * 오늘 수업 레슨 가이드 패널 (Phase 6a+6b, §5-12/§5-13/§5-신규).
 * 교과정보(grade 파싱) + 생각열기(레슨 설명/포인트) + 핵심 체크(문장 + 5점 입력)를 실데이터로 노출한다.
 * 개념·원리 이해도(6b)는 규칙 기반 분석 결과를 노출한다.
 */
export function CoachingGuidePanel({ learnerId, lessonId, lessonCompleted, surface = 'page' }: Props) {
  const { t } = useTranslation('coaching')
  const user = useAuthStore((s) => s.user)
  const canEdit = hasLcPermission(user ?? undefined, 'COACHING')
  const { data: guide, isLoading } = useCoachingGuide(learnerId, lessonId)
  const rate = useRateEssentialCheck(learnerId)
  const containerClass = surface === 'dialog'
    ? 'flex flex-col gap-3'
    : 'sticky top-[120px] flex max-h-[calc(100vh-140px)] flex-col gap-3 overflow-y-auto'

  return (
    <aside
      className={containerClass}
      aria-label={t('coachingGuide.title')}
    >
      <h3 className="text-sm font-semibold text-gray-900">{t('coachingGuide.title')}</h3>

      {lessonId == null ? (
        <Card>
          <p className="text-sm text-gray-500">{t('coachingGuide.noLesson')}</p>
        </Card>
      ) : isLoading || !guide ? (
        <Card>
          <p className="text-sm text-gray-500" role="status" aria-live="polite">
            {t('coachingGuide.loading')}
          </p>
        </Card>
      ) : (
        <>
          {/* 교과정보 */}
          <Section title={t('coachingGuide.subjectInfo.title')}>
            {guide.subjectInfo ? (
              <SubjectInfoBody info={guide.subjectInfo} />
            ) : (
              <Empty>{t('coachingGuide.subjectInfo.empty')}</Empty>
            )}
          </Section>

          {/* 생각열기 */}
          <Section title={t('coachingGuide.thinkingOpener.title')}>
            {guide.thinkingOpener && (guide.thinkingOpener.text || guide.thinkingOpener.shortPoint) ? (
              <div className="space-y-2">
                {guide.thinkingOpener.text && (
                  <div>
                    <p className="text-[10px] font-medium uppercase text-gray-500">
                      {t('coachingGuide.thinkingOpener.lessonDesc')}
                    </p>
                    <WarmupTextView
                      text={guide.thinkingOpener.text}
                      className="mt-1 whitespace-pre-wrap text-xs text-gray-800"
                    />
                  </div>
                )}
                {guide.thinkingOpener.shortPoint && (
                  <div>
                    <p className="text-[10px] font-medium uppercase text-gray-500">
                      {t('coachingGuide.thinkingOpener.keyPoint')}
                    </p>
                    <WarmupTextView
                      text={stripShortPointPrefix(guide.thinkingOpener.shortPoint)}
                      className="mt-1 text-xs text-gray-800"
                    />
                  </div>
                )}
              </div>
            ) : (
              <Empty>{t('coachingGuide.thinkingOpener.empty')}</Empty>
            )}
          </Section>

          {/* 핵심 체크 + 5점 입력 */}
          <Section title={t('coachingGuide.essentialCheck.title')}>
            {guide.essentialChecks.length > 0 ? (
              <ul className="space-y-2">
                {guide.essentialChecks.map((check) => (
                  <li key={check.checkNo} className="rounded border border-gray-200 bg-white p-2">
                    <p className="text-xs text-gray-800">
                      <LearningContentText
                        text={
                          check.text ??
                          check.label ??
                          t('coachingGuide.essentialCheck.untitled', { no: check.checkNo })
                        }
                      />
                    </p>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-[10px] text-gray-500">
                        {t('coachingGuide.essentialCheck.ratingLabel')}
                      </span>
                      <EssentialCheckRatingInput
                        value={check.score}
                        disabled={!canEdit || rate.isPending}
                        onChange={(score) =>
                          rate.mutate({ lessonId: guide.lessonId, checkNo: check.checkNo, score })
                        }
                      />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>{t('coachingGuide.essentialCheck.empty')}</Empty>
            )}
          </Section>

          {/* 개념·원리 이해도 (6b — §5-12=A 규칙 기반) */}
          <Section title={t('coachingGuide.understanding.title')}>
            {!lessonCompleted ? (
              <Empty>{t('coachingGuide.understanding.afterComplete')}</Empty>
            ) : guide.understanding.pending ? (
              <Empty>{t('coachingGuide.understanding.insufficient')}</Empty>
            ) : (
              <UnderstandingBody understanding={guide.understanding} />
            )}
          </Section>
        </>
      )}
    </aside>
  )
}

function Card({ children }: { children: ReactNode }) {
  return <div className="rounded-lg border bg-white p-4">{children}</div>
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border bg-white p-3">
      <h4 className="mb-2 text-xs font-semibold text-gray-900">{title}</h4>
      {children}
    </section>
  )
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="text-xs italic text-gray-500">{children}</p>
}

/** "초등 6학년 1학기 2단원", or the raw label when the structured parts are missing. */
export function useSubjectInfoHeading(info: SubjectInfo | null | undefined): string {
  const { t } = useTranslation('coaching')
  if (!info) return ''
  const parts: string[] = []
  if (info.school) parts.push(info.school)
  if (info.grade != null) parts.push(t('coachingGuide.subjectInfo.grade', { n: info.grade }))
  if (info.semester != null) parts.push(t('coachingGuide.subjectInfo.semester', { n: info.semester }))
  if (info.unit != null) parts.push(t('coachingGuide.subjectInfo.unit', { n: info.unit }))
  return parts.join(' ') || (info.raw ?? '')
}

export function SubjectInfoBody({ info }: { info: SubjectInfo }) {
  const heading = useSubjectInfoHeading(info)
  return (
    <div className="space-y-1">
      {heading && <p className="text-xs font-medium text-gray-800">{heading}</p>}
      {info.unitName && <p className="text-xs text-gray-600">{info.unitName}</p>}
    </div>
  )
}
